import { clonePoint, inBounds, key } from './geometry';
import { PERFECT_CUT, refMWFor, statsFor, tierFor } from './scoring';
import type { Mode, Point, RunRecord, ScoredPuzzle, Solution, Stats, Tier } from './types';

/** Why a tap didn't build anything. */
export type RejectReason = 'water' | 'mtn' | 'occupied' | 'out-of-towers';

export type TapResult =
  | { type: 'built'; at: Point }
  | { type: 'removed'; at: Point }
  | { type: 'rejected'; reason: RejectReason };

/** The player's best completed layout on this grid. */
export interface BestRun {
  wire: number;
  mw: number;
  towers: Point[];
}

/** What changed after a tap, for the UI to react to. */
export interface Evaluation {
  stats: Stats;
  /** MW of the layout currently on the board, or `null` if it's dark. */
  mw: number | null;
  /** Every house is lit right now. */
  allLit: boolean;
  /** This tap completed the grid for the first time. */
  justSolved: boolean;
  /** The best layout got shorter — true on the first solve too. */
  improved: boolean;
  /** This move reached the perfect grid for the first time this session. */
  justPerfect: boolean;
}

/**
 * One grid in play: the towers on the board, the best layout so far, and the
 * cold-read MW. Pure — no DOM, no storage — so the rules can be tested
 * without a browser.
 *
 * Nothing here counts moves. Exploration is never taxed (GDD Pillar 3), and a
 * count that is permanent and eventually public is a tax however quietly it
 * runs (GDD §9).
 */
export class GameSession {
  readonly puzzle: ScoredPuzzle;
  readonly mode: Mode;
  /** Which daily grid this is; `null` in practice and in the training yard. */
  readonly day: number | null;

  placed: Point[] = [];
  /** The grid has been completed at least once. */
  solvedOnce = false;
  best: BestRun | null = null;
  /** MW of the first layout that lit every house — the cold read. */
  firstMW: number | null = null;

  /**
   * Every MW reading that was a personal best, oldest first: the cold read,
   * then each improvement. The share prints these as its milestones.
   * Consecutive readings that print the same MW are folded together — a
   * milestone that repeats its neighbour says nothing.
   */
  private trail: number[] = [];

  /** The perfect grid has already been reached, so it can't be reached again. */
  private perfectSeen = false;

  /** A tower has come down on this grid. Once true, flawless is out of reach. */
  private removedEver = false;
  /** Latched the moment the perfect grid was reached with nothing ever removed. */
  private flawlessEarned = false;
  /** The reference was studied while this grid was still short of perfect. */
  private studiedEarly = false;

  /**
   * The naive line's readings, set by `prebuild()`: a strategy drill opens
   * on its naive line and completes when the player's line delivers more
   * than it does (GDD §4.6). Null on a board no line was prebuilt onto —
   * every other mode, and the first-light primer.
   */
  private naiveWire: number | null = null;
  private naiveMW: number | null = null;

  constructor(puzzle: ScoredPuzzle, mode: Mode, day: number | null) {
    this.puzzle = puzzle;
    this.mode = mode;
    this.day = day;
  }

  /** The naive line's delivery, or `null` outside the training yard. */
  get naiveLineMW(): number | null {
    return this.naiveMW;
  }

  /** The naive line's wire, or `null` outside the training yard. */
  get naiveLineWire(): number | null {
    return this.naiveWire;
  }

  /** The tuning trajectory, oldest reading first. */
  get mwTrail(): number[] {
    return [...this.trail];
  }

  /**
   * Reached the perfect grid having never taken a tower down (GDD §3.6).
   *
   * Latched at the moment it was earned, not recomputed: tuning on past a
   * flawless perfect cannot take it away, and nothing after that moment can
   * grant it either.
   */
  get flawless(): boolean {
    return this.flawlessEarned;
  }

  /** A tower has come down here at some point. */
  get everRemoved(): boolean {
    return this.removedEver;
  }

  /** The reference was studied before this grid was perfected (GDD §4.3). */
  get studied(): boolean {
    return this.studiedEarly;
  }

  /**
   * Record that the player looked at the reference.
   *
   * Only a look taken while the grid is still short of perfect marks it:
   * after a perfect there is nothing left to spoil.
   */
  markStudied(): void {
    if (!this.matchedReference) this.studiedEarly = true;
  }

  /** Pick up how this grid was played in an earlier session. */
  restoreRun(record: RunRecord | null): void {
    if (!record) return;
    this.removedEver = Boolean(record.everRemoved);
    this.flawlessEarned = Boolean(record.flawless);
    this.studiedEarly = Boolean(record.studied);
  }

  /** How this grid has been played, as stored. */
  get runRecord(): RunRecord {
    return {
      everRemoved: this.removedEver,
      flawless: this.flawlessEarned,
      studied: this.studiedEarly,
    };
  }

  /** Is a cell blocked for building — terrain, the plant, or a house? */
  blockedAt(p: Point): boolean {
    const k = key(p);
    return (
      Boolean(this.puzzle.obstacles[k]) ||
      k === key(this.puzzle.plant) ||
      this.puzzle.houses.some((h) => key(h) === k)
    );
  }

  hasTowerAt(p: Point): boolean {
    return this.placed.some((t) => key(t) === key(p));
  }

  /** Build or remove a tower. Removal always wins over the par limit. */
  tap(p: Point): TapResult {
    const idx = this.placed.findIndex((t) => key(t) === key(p));
    if (idx >= 0) {
      this.placed.splice(idx, 1);
      this.removedEver = true;
      return { type: 'removed', at: p };
    }
    const terrain = this.puzzle.obstacles[key(p)];
    if (terrain) return { type: 'rejected', reason: terrain };
    if (this.blockedAt(p)) return { type: 'rejected', reason: 'occupied' };
    if (Number.isFinite(this.puzzle.par) && this.placed.length >= this.puzzle.par) {
      return { type: 'rejected', reason: 'out-of-towers' };
    }
    this.placed.push(clonePoint(p));
    return { type: 'built', at: p };
  }

  /**
   * Put the naive line a strategy drill opens on (GDD §4.6) onto the board.
   * Not a restore: nothing is latched, because the layout is the drill's,
   * not the player's — beating it is still ahead. The line's own readings
   * are taken here, and they are what `evaluate()` gates completion on.
   */
  prebuild(towers: Point[]): void {
    const seen = new Set<string>();
    this.placed = [];
    for (const t of towers) {
      const k = key(t);
      if (!inBounds(t) || this.blockedAt(t) || seen.has(k)) continue;
      seen.add(k);
      this.placed.push(clonePoint(t));
    }
    const line = statsFor(this.puzzle, this.placed);
    if (line.ok) {
      this.naiveWire = line.wire;
      this.naiveMW = refMWFor(this.puzzle.ref.wire, line.wire);
    }
  }

  /**
   * Restore a layout from storage without treating it as a fresh solve.
   *
   * Saved towers are checked against the board they land on rather than
   * trusted: a save can be corrupt, and a tower rendered onto water would be
   * a scoreboard nobody could explain.
   */
  restore(towers: Point[], firstMW: number | null, trail?: number[]): void {
    const seen = new Set<string>();
    this.placed = [];
    for (const t of towers) {
      const k = key(t);
      if (!inBounds(t) || this.blockedAt(t) || seen.has(k)) continue;
      seen.add(k);
      this.placed.push(clonePoint(t));
    }

    const s = statsFor(this.puzzle, this.placed);
    if (!s.ok) return;
    const mw = refMWFor(this.puzzle.ref.wire, s.wire);
    this.best = { wire: s.wire, mw, towers: this.placed.map(clonePoint) };
    this.solvedOnce = true;
    const cold = firstMW ?? mw;
    this.firstMW = cold;
    // A trail the game never recorded is not invented (GDD Pillar 4) — but the
    // cold read is not invention, it is the one milestone every save has kept
    // since before trails existed. Without it the next improvement would open
    // the stored trail at some middle reading, and the share would print a run
    // starting somewhere the player never was.
    this.trail = trail?.length ? [...trail] : [cold];
    // Reaching the perfect grid is a once-per-grid moment. Coming back to a
    // grid that is already perfect is not reaching it.
    this.perfectSeen = this.matchedReference;
  }

  /**
   * Score the board as it stands and fold it into the running best.
   * Idempotent: calling twice without a tap in between changes nothing.
   */
  evaluate(): Evaluation {
    const stats = statsFor(this.puzzle, this.placed);
    const allLit = stats.ok && this.puzzle.houses.length > 0;
    const mw = allLit ? refMWFor(this.puzzle.ref.wire, stats.wire) : null;

    let justSolved = false;
    let improved = false;

    // On a board that opened lit on a naive line, lit is the starting
    // position, not a solve: nothing latches until the player's line delivers
    // more than the naive one. Compared in quantized MW, not raw wire — a
    // completion the game's own readouts can't show didn't happen for the
    // player. A board nothing was prebuilt onto keeps the ordinary rule.
    const beatsNaive = this.naiveMW === null || (mw !== null && mw > this.naiveMW);

    if (allLit && mw !== null && beatsNaive) {
      if (!this.solvedOnce && this.firstMW === null) this.firstMW = mw;
      if (!this.best || stats.wire < this.best.wire - 1e-9) {
        improved = true;
        this.best = { wire: stats.wire, mw, towers: this.placed.map(clonePoint) };
        if (this.trail[this.trail.length - 1] !== mw) this.trail.push(mw);
      }
      if (!this.solvedOnce) {
        this.solvedOnce = true;
        justSolved = true;
      }
    }

    // Latch the perfect grid after the best has been updated, so the move that
    // achieved it is the one that fires the payoff.
    let justPerfect = false;
    if (!this.perfectSeen && this.matchedReference) {
      this.perfectSeen = true;
      this.flawlessEarned = !this.removedEver;
      justPerfect = true;
    }

    return { stats, mw, allLit, justSolved, improved, justPerfect };
  }

  /** Rank of the best layout so far. */
  tier(): Tier | null {
    return this.best ? tierFor(this.puzzle.ref.wire, this.best.wire) : null;
  }

  /** The best layout matches the solver's reference grid. */
  get matchedReference(): boolean {
    return Boolean(this.best) && this.puzzle.ref.wire / this.best!.wire >= PERFECT_CUT;
  }

  /** The best layout is shorter than the solver's reference grid. */
  get beatReference(): boolean {
    return Boolean(this.best) && this.best!.wire < this.puzzle.ref.wire - 1e-6;
  }

  /** Solved with towers to spare. */
  get underPar(): boolean {
    return Boolean(this.best) && this.best!.towers.length < this.puzzle.par;
  }

  get reference(): Solution {
    return this.puzzle.ref;
  }
}
